import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ChatWidgetEmbedProps } from "@/types/chat-widget";
import WidgetPreview from "./WidgetPreview";
import { useEmbedCode } from "./hooks/useEmbedCode";
import { ScriptEmbed } from "./ScriptEmbed";
import { IFrameEmbed } from "./IFrameEmbed";
import { ReactEmbed } from "./ReactEmbed";
import { LeadConversionsSettings } from "./LeadConversionsSettings";

export const ChatWidgetEmbed = ({ genie }: ChatWidgetEmbedProps) => {
  const { scriptEmbedCode, iframeEmbedCode, iframeReactCode, baseUrl, urlName, primaryColor } = useEmbedCode(
    genie,
    "classic",
    "medium",
    genie.branding?.embedStyle?.widgetPosition || "bottom-right",
    genie.branding?.embedStyle?.widgetButtonLabel || `Chat with ${genie.name}`,
    undefined,
    "chat",
  );

  return (
    <div className="space-y-6 p-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Chat Widget</h2>
        <p className="mt-1 text-sm text-gray-600">Open straight into a text conversation, with your Genie’s logo and colours.</p>
      </div>
      <div className="grid items-start gap-6 xl:grid-cols-2">
        <div className="min-w-0 space-y-4">
          <WidgetPreview genie={genie} selectedTemplate="classic" primaryColor={primaryColor} embedStyle={genie.branding?.embedStyle} chatOnly />
          <p className="text-sm text-gray-600">Uses your saved popup appearance and chat styling. Update these in Standard Widget → Popup Appearance.</p>
          <Button asChild variant="outline" className="min-h-11 gap-2">
            <a href={`${baseUrl}/live-agent/${encodeURIComponent(urlName)}?embed=true&mode=chat`} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> Test chat widget
            </a>
          </Button>
          <p className="text-xs text-gray-500">Opens a real conversation. Your Genie must be active and accessible to website visitors.</p>
        </div>
        <div className="min-w-0 space-y-4 rounded-xl border border-genie-border bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-700">Add to your website</h3>
          <p className="text-sm text-gray-600">Visitors can type and share attachments without voice controls or microphone access.</p>
          <Tabs defaultValue="script">
            <TabsList aria-label="Chat widget installation format" className="grid h-auto w-full grid-cols-3">
              <TabsTrigger value="script" className="min-h-11">JavaScript</TabsTrigger>
              <TabsTrigger value="html" className="min-h-11">HTML</TabsTrigger>
              <TabsTrigger value="react" className="min-h-11">React</TabsTrigger>
            </TabsList>
            <TabsContent value="script"><ScriptEmbed embedCode={scriptEmbedCode} /></TabsContent>
            <TabsContent value="html"><IFrameEmbed embedCode={iframeEmbedCode} /></TabsContent>
            <TabsContent value="react"><ReactEmbed embedCode={iframeReactCode} /></TabsContent>
          </Tabs>
          <LeadConversionsSettings genie={genie} />
        </div>
      </div>
    </div>
  );
};
